import { applicationPath } from '@vivaporta/work-areas';
import { isCareIndustryPageKey, type CareIndustryPageKey } from './careIndustryLabels.js';
import { createCareIndustryPages } from './careIndustryPages.js';
import { createRetailIndustryPages } from './retailIndustryPages.js';
import { isRetailIndustryPageKey } from './retailIndustryLabels.js';
import type { LandingLocale } from './landingContentTypes.js';
import type { StaticPageContent, StaticPageHeroGalleryItem } from './staticPageContent.js';
import { resolveApplicationPortalBase } from './landingContentShared.js';
import {
  industryPageLabel,
  newIndustryPageKeys,
  type IndustryPageKey,
  type NewIndustryPageKey,
} from './industryPages.js';
import electricianPanel from '../../img/industries/electrician-panel.jpg';
import electricianInstallation from '../../img/industries/electrician-installation.jpg';
import constructionCivil from '../../img/industries/construction-civil.jpg';
import constructionDrywall from '../../img/industries/construction-drywall.jpg';
import welderWorkshop from '../../img/industries/welder-workshop.jpg';
import welderMetal from '../../img/industries/welder-metal.jpg';
import cncMachine from '../../img/industries/cnc-machine.jpg';
import cncTools from '../../img/industries/cnc-tools.jpg';
import shkHeating from '../../img/industries/shk-heating.jpg';
import shkPipes from '../../img/industries/shk-pipes.jpg';
import truckDriver from '../../img/industries/truck-driver.jpg';
import truckLogistics from '../../img/industries/truck-logistics.jpg';
import care from '../../img/industries/care-portrait.webp';
import nurse from '../../img/industries/care-team.webp';
import craft from '../../img/industries/craft-portrait.webp';
import retail from '../../img/industries/retail-portrait.webp';
import fresh from '../../img/globus-jobs/globus-fresh-counter.webp';
import gastro from '../../img/hero/industry-gastro-v2.png';
import kitchen from '../../img/globus-jobs/globus-gastronomy.webp';

const photos: Record<IndustryPageKey, readonly [string, string]> = {
  ...Object.fromEntries(Object.entries(createRetailIndustryPages()).map(([key,page])=>[key,[page.heroGallery![0]!.image,page.heroGallery![1]!.image] as const])) as Record<'gastronomie'|'baecker'|'metzger'|'frischetheke',readonly [string,string]>,
  ...Object.fromEntries(Object.entries(createCareIndustryPages()).map(([key,page])=>[key,[page.heroGallery![0]!.image,page.heroGallery![1]!.image] as const])) as Record<CareIndustryPageKey, readonly [string,string]>,
  pflege: [care, nurse],
  handwerk: [craft, constructionDrywall],
  einzelhandel: [retail, fresh],
  gastro: [gastro, kitchen],
  elektriker: [electricianPanel, electricianInstallation],
  bauwesen: [constructionCivil, constructionDrywall],
  schweisser: [welderWorkshop, welderMetal],
  cnc: [cncMachine, cncTools],
  shk: [shkHeating, shkPipes],
  'lkw-fahrer': [truckDriver, truckLogistics],
};

const existingPhotoCopy = {
  de: {
    pflege: [
      ['Pflegealltag', 'Pflegefachkraft in Arbeitskleidung in einer Gesundheitseinrichtung.'],
      ['Im Team', 'Porträt einer Pflegefachkraft.'],
    ],
    handwerk: [
      ['Handwerk', 'Fachkraft in einer Werkstatt mit Werkzeugen.'],
      ['Innenausbau', 'Handwerker bei Trockenbauarbeiten.'],
    ],
    einzelhandel: [
      ['Verkauf', 'Fachkraft im Verkauf an einer Kasse.'],
      ['Frischetheke', 'Mitarbeiter an einer Frischetheke.'],
    ],
  },
  en: {
    pflege: [
      ['Care work', 'Care professional in work clothing in a healthcare setting.'],
      ['Working together', 'Portrait of a care professional.'],
    ],
    handwerk: [
      ['Skilled trades', 'Skilled professional in a workshop with tools.'],
      ['Interior work', 'Tradesperson fitting drywall.'],
    ],
    einzelhandel: [
      ['Sales', 'Retail professional at a checkout.'],
      ['Fresh counter', 'Employee at a fresh-food counter.'],
    ],
  },
  tr: {
    pflege: [
      ['Bakım hayatı', 'Sağlık kuruluşunda iş kıyafetli bir bakım çalışanı.'],
      ['Ekipte', 'Bir bakım uzmanının portresi.'],
    ],
    handwerk: [
      ['Zanaat', 'Aletlerin bulunduğu atölyede bir uzman.'],
      ['İç mekân işleri', 'Alçıpan montajı yapan bir usta.'],
    ],
    einzelhandel: [
      ['Satış', 'Kasada çalışan bir satış uzmanı.'],
      ['Taze ürün reyonu', 'Taze ürün reyonunda çalışan bir görevli.'],
    ],
  },
};

interface IndustryCopy {
  title: string;
  intro: string;
  areas: readonly (readonly [string, string])[];
  preparation: string;
  photoAlts: readonly [string, string];
}

const copy: Record<LandingLocale, Record<NewIndustryPageKey, IndustryCopy>> = {
  de: {
    elektriker: {
      title: 'Deine Zukunft als Elektriker oder Elektroniker.',
      intro:
        'Du kennst dich mit Elektrik oder Elektronik aus? Wir schauen gemeinsam, wie deine Erfahrung zu einem Betrieb in Deutschland passt – von der ersten Bewerbung bis zum Ankommen.',
      areas: [
        [
          'Installation',
          'Leitungen verlegen, Anschlüsse vorbereiten und elektrische Anlagen fachgerecht installieren.',
        ],
        ['Elektronik', 'Bauteile, Steuerungen und technische Systeme prüfen und betreuen.'],
        ['Wartung', 'Fehler systematisch finden, Anlagen instand halten und sicher dokumentieren.'],
      ],
      preparation:
        'Erzähle uns, welche Ausbildung du hast, an welchen Anlagen du gearbeitet hast und welche Aufgaben du sicher beherrschst. Zeugnisse und Tätigkeitsnachweise helfen bei der Einordnung. Welche Anerkennung und Sprachkenntnisse für eine konkrete Stelle nötig sind, klären wir individuell.',
      photoAlts: [
        'Elektriker arbeitet mit Schutzausrüstung an einem geöffneten Schaltschrank.',
        'Fachkraft prüft die Verkabelung einer elektrischen Verteilung.',
      ],
    },
    bauwesen: {
      title: 'Bauwesen. Baue an deiner Zukunft.',
      intro:
        'Ob Tiefbau, Rohrbau, Kanalbau oder Trockenbau: Dein praktisches Können zählt. Wir bringen Struktur in deine Bewerbung und besprechen, welcher Betrieb und welche Aufgaben zu dir passen.',
      areas: [
        ['Tief- und Kanalbau', 'Auf Baustellen an Infrastruktur, Entwässerung und Versorgungsnetzen mitarbeiten.'],
        ['Rohrbau', 'Rohrleitungen vorbereiten, verlegen und montieren – passend zu deiner bisherigen Erfahrung.'],
        ['Trockenbau', 'Innenräume gestalten, Wände und Decken montieren und sauber nach Plan arbeiten.'],
      ],
      preparation:
        'Nenne uns deine bisherigen Bauprojekte, deine Aufgaben und die Werkzeuge oder Maschinen, mit denen du vertraut bist. Auch praktische Erfahrung ohne Hochschulabschluss kannst du im Formular angeben. Sicherheitsunterweisungen, Sprache und die Anforderungen des Betriebs besprechen wir vor einem möglichen Start.',
      photoAlts: [
        'Bauarbeiter mit Schutzhelmen und Warnwesten auf einer Baustelle.',
        'Handwerker montiert eine Trockenbauwand im Innenausbau.',
      ],
    },
    schweisser: {
      title: 'Schweißer. Dein Handwerk verbindet.',
      intro:
        'Du arbeitest gern mit Metall und bringst Schweißerfahrung mit? Zeig uns, was du kannst. Wir begleiten dich auf dem Weg zu einem passenden Arbeitsplatz in Deutschland.',
      areas: [
        ['Schweißverfahren', 'Deine Erfahrung mit MIG, MAG, WIG oder E-Hand steht im Mittelpunkt der Bewerbung.'],
        ['Metallbau', 'Bauteile nach Zeichnung vorbereiten, ausrichten und zu stabilen Konstruktionen verbinden.'],
        ['Qualität', 'Schweißnähte sorgfältig ausführen, Ergebnisse prüfen und Schutzvorgaben einhalten.'],
      ],
      preparation:
        'Teile uns deine Schweißverfahren, Werkstoffe und bisherigen Einsatzbereiche mit. Vorhandene Schweißerprüfungen oder Zertifikate kannst du als Nachweis einreichen. Welche Prüfungen für eine konkrete Aufgabe verlangt werden, klären wir mit dem Betrieb; eine Bewerbung ersetzt keine fachliche Zulassung.',
      photoAlts: [
        'Schweißer mit Schutzhelm arbeitet in einer Werkstatt an einem Metallstück.',
        'Schweißarbeiten in einer Fertigungshalle mit sichtbarem Funkenflug.',
      ],
    },
    cnc: {
      title: 'CNC-Zerspanung. Präzision mit Zukunft.',
      intro:
        'Drehen, Fräsen, Einrichten oder Programmieren: Erzähl uns von deiner Arbeit an CNC-Maschinen. Gemeinsam prüfen wir, wo dein Können in Deutschland gefragt ist.',
      areas: [
        ['Drehen und Fräsen', 'Werkstücke nach Zeichnung bearbeiten und mit passenden Werkzeugen fertigen.'],
        ['Maschinen einrichten', 'Werkzeuge wechseln, Programme einfahren und Bearbeitungsschritte überwachen.'],
        ['Maßhaltigkeit', 'Bauteile messen, Toleranzen prüfen und die Fertigungsqualität dokumentieren.'],
      ],
      preparation:
        'Beschreibe die Maschinen, Steuerungen und Materialien, mit denen du gearbeitet hast. Wichtig ist auch, ob du Maschinen bedienst, selbst einrichtest oder Programme erstellst. Ausbildungsnachweise und Arbeitszeugnisse machen dein Profil für einen Betrieb leichter einschätzbar.',
      photoAlts: [
        'CNC-Maschine bei der Bearbeitung eines Werkstücks in einer Werkstatt.',
        'Metall wird auf einer Drehmaschine in einer Werkstatt bearbeitet.',
      ],
    },
    shk: {
      title: 'Deine Zukunft in Sanitär, Heizung & Klima.',
      intro:
        'Wasser, Wärme, gutes Raumklima: Du hältst wichtige Technik am Laufen. Wir begleiten deine Bewerbung im Bereich Sanitär, Heizung und Klima für einen passenden Start in Deutschland.',
      areas: [
        ['Sanitär', 'Wasserleitungen, Armaturen und sanitäre Anlagen montieren und instand halten.'],
        ['Heizung', 'Heizungsanlagen und ihre Komponenten installieren, warten und prüfen.'],
        ['Klima und Service', 'An Raumklima- und Lüftungstechnik arbeiten und Störungen systematisch eingrenzen.'],
      ],
      preparation:
        'Gib an, mit welchen Anlagen und Systemen du Erfahrung hast und ob dein Schwerpunkt Montage, Wartung oder Kundendienst ist. Für bestimmte Tätigkeiten sind zusätzliche Nachweise erforderlich. Wir klären die Anforderungen einer Stelle vorab und berücksichtigen dabei Ausbildung, Sprache und praktische Erfahrung.',
      photoAlts: [
        'Techniker kontrolliert eine Heizungsanlage in einem Technikraum.',
        'Sanitärfachkraft arbeitet mit einem Rohrschlüssel an einer Rohrleitung.',
      ],
    },
    'lkw-fahrer': {
      title: 'LKW-Fahrer. Bring deine Zukunft auf die Straße.',
      intro:
        'Du hast Erfahrung am Steuer eines LKW? Wir besprechen mit dir, wie dein Weg in den Transport in Deutschland aussehen kann – mit klaren Anforderungen und persönlicher Begleitung.',
      areas: [
        ['Gütertransport', 'Fahrten zuverlässig vorbereiten und Waren sicher zum Ziel bringen.'],
        ['Be- und Entladung', 'Mit dem Lager zusammenarbeiten und auf eine korrekte Ladungssicherung achten.'],
        [
          'Fahrzeug und Planung',
          'Fahrzeugzustand, Touren und Dokumente im Blick behalten und verständlich kommunizieren.',
        ],
      ],
      preparation:
        'Teile uns deine Führerscheinklassen, das Ausstellungsland, deine Fahrpraxis und vorhandene Qualifikationsnachweise mit. Ob eine Umschreibung, Berufskraftfahrerqualifikation oder weitere Unterlagen nötig sind, muss vor einem Einsatz individuell geklärt werden. Die Bewerbung ist der erste Schritt dieser Prüfung.',
      photoAlts: [
        'Berufskraftfahrer sitzt mit Warnweste im Fahrerhaus eines LKW.',
        'LKW-Fahrer in Warnkleidung an einem Fahrzeug im Logistikbereich.',
      ],
    },
  },
  en: {
    elektriker: {
      title: 'Your future in electrical & electronics work.',
      intro:
        'Experienced in electrical work or electronics? Together, we explore how your skills fit an employer in Germany, with personal guidance from your application through arrival.',
      areas: [
        ['Installation', 'Lay cables, prepare connections and install electrical systems professionally.'],
        ['Electronics', 'Inspect and maintain components, controls and technical systems.'],
        ['Maintenance', 'Find faults systematically, maintain equipment and document work safely.'],
      ],
      preparation:
        'Tell us about your training, the systems you have worked on and the tasks you handle confidently. Qualifications and employment records help us understand your experience. Recognition and language requirements depend on the particular role and are reviewed individually.',
      photoAlts: [
        'Electrician wearing protective equipment works on an open electrical cabinet.',
        'Technician checks the wiring of an electrical distribution panel.',
      ],
    },
    bauwesen: {
      title: 'Construction. Build your future.',
      intro:
        'Civil engineering, pipework, sewer construction or drywall: your practical skills matter. We help structure your application and discuss which tasks and employers fit your experience.',
      areas: [
        ['Civil engineering', 'Work on infrastructure, drainage and utility networks on construction sites.'],
        ['Pipework', 'Prepare, lay and assemble piping in line with your previous experience.'],
        ['Drywall', 'Fit walls and ceilings and carry out interior work carefully to plan.'],
      ],
      preparation:
        'Describe your previous building projects, tasks and the tools or machines you know. You can apply with practical experience without a university degree. Before a possible start, we discuss safety training, language and the employer’s requirements.',
      photoAlts: [
        'Construction workers wearing hard hats and high-visibility vests on a building site.',
        'Tradesperson fits a drywall panel during interior construction.',
      ],
    },
    schweisser: {
      title: 'Welding. Your craft makes connections.',
      intro:
        'Enjoy working with metal and have welding experience? Show us what you can do. We guide you towards a suitable workplace in Germany.',
      areas: [
        ['Welding methods', 'Tell us about your experience with MIG, MAG, TIG or manual metal arc welding.'],
        ['Metalwork', 'Prepare and align parts to drawings and join them into stable structures.'],
        ['Quality', 'Produce careful welds, check results and follow protective measures.'],
      ],
      preparation:
        'Tell us which welding methods and materials you use and where you have worked. You can submit existing welding qualifications and certificates. We clarify the tests needed for a specific task with the employer; applying does not replace professional certification.',
      photoAlts: [
        'Welder wearing a protective helmet works on a metal part in a workshop.',
        'Welding work with visible sparks in a manufacturing hall.',
      ],
    },
    cnc: {
      title: 'CNC machining. Precision with a future.',
      intro:
        'Turning, milling, setting up or programming: tell us about your CNC experience. Together, we explore where your skills could fit in Germany.',
      areas: [
        ['Turning and milling', 'Machine parts to drawings using suitable tools and processes.'],
        ['Machine setup', 'Change tools, run programs and monitor machining operations.'],
        ['Dimensional accuracy', 'Measure components, check tolerances and document production quality.'],
      ],
      preparation:
        'Describe the machines, controls and materials you know. Explain whether you operate machines, set them up or create programs yourself. Training and employment records help employers assess your experience.',
      photoAlts: [
        'CNC machine processes a workpiece in an industrial workshop.',
        'A metal workpiece is machined on a lathe in a workshop.',
      ],
    },
    shk: {
      title: 'Your future in plumbing, heating & cooling.',
      intro:
        'Water, warmth and a comfortable indoor climate: you keep essential systems running. We support your application in plumbing, heating and air conditioning for a suitable start in Germany.',
      areas: [
        ['Plumbing', 'Install and maintain water pipes, fittings and sanitary systems.'],
        ['Heating', 'Install, service and inspect heating systems and components.'],
        ['Cooling and service', 'Work on ventilation and climate systems and trace faults systematically.'],
      ],
      preparation:
        'Tell us which systems you have worked on and whether your focus is installation, maintenance or field service. Some tasks require additional qualifications. We review the role’s requirements in advance, considering your training, language skills and practical experience.',
      photoAlts: [
        'Technician checks heating equipment in a plant room.',
        'Plumbing specialist uses a pipe wrench to work on a pipe.',
      ],
    },
    'lkw-fahrer': {
      title: 'Truck driving. Take your future on the road.',
      intro:
        'Have experience behind the wheel of a truck? We discuss your route into transport work in Germany with clear requirements and personal guidance.',
      areas: [
        ['Freight transport', 'Prepare journeys reliably and deliver goods safely.'],
        ['Loading and unloading', 'Work with warehouse teams and secure loads correctly.'],
        ['Vehicle and planning', 'Keep track of vehicle condition, routes and documents and communicate clearly.'],
      ],
      preparation:
        'Tell us your licence categories, issuing country, driving experience and any professional driver qualifications. Licence conversion, professional qualifications and further documents must be checked individually before you can start work. The application begins that review.',
      photoAlts: [
        'Professional truck driver wearing a high-visibility vest sits in a truck cab.',
        'Truck driver in high-visibility clothing beside a vehicle at a logistics facility.',
      ],
    },
  },
  tr: {
    elektriker: {
      title: 'Elektrik ve elektronikte geleceğin.',
      intro:
        'Elektrik veya elektronik alanında deneyimin mi var? Deneyiminin Almanya’daki bir işletmeyle nasıl örtüştüğünü birlikte değerlendiriyor, başvurudan varışa kadar sana eşlik ediyoruz.',
      areas: [
        ['Montaj', 'Kablo döşeme, bağlantıları hazırlama ve elektrik sistemlerini doğru şekilde kurma.'],
        ['Elektronik', 'Bileşenleri, kumandaları ve teknik sistemleri kontrol etme ve bakımını yapma.'],
        ['Bakım', 'Arızaları sistemli şekilde bulma, ekipmanı çalışır tutma ve işleri güvenli biçimde belgeleme.'],
      ],
      preparation:
        'Eğitimini, çalıştığın sistemleri ve güvenle yaptığın işleri anlat. Diplomalar ve çalışma belgeleri deneyimini anlamamıza yardımcı olur. Belirli bir pozisyon için gereken denklik ve dil düzeyini bireysel olarak değerlendiririz.',
      photoAlts: [
        'Koruyucu ekipmanlı bir elektrikçi açık bir elektrik panosunda çalışıyor.',
        'Bir teknisyen elektrik dağıtım panosundaki kabloları kontrol ediyor.',
      ],
    },
    bauwesen: {
      title: 'İnşaat. Geleceğini sağlam temellerle kur.',
      intro:
        'Altyapı, boru döşeme, kanalizasyon veya alçıpan: pratik becerilerin önemli. Başvurunu birlikte yapılandırıyor, sana uygun işleri ve işletmeleri konuşuyoruz.',
      areas: [
        ['Altyapı ve kanalizasyon', 'Şantiyelerde altyapı, drenaj ve dağıtım şebekeleri üzerinde çalışma.'],
        ['Boru işleri', 'Deneyimine uygun boruları hazırlama, döşeme ve birleştirme.'],
        ['Alçıpan', 'İç mekânlarda duvar ve tavanları plana uygun, özenli biçimde monte etme.'],
      ],
      preparation:
        'Çalıştığın inşaat projelerini, görevlerini ve bildiğin alet veya makineleri anlat. Üniversite diploman olmadan da pratik deneyiminle başvurabilirsin. Olası bir başlangıçtan önce iş güvenliği eğitimlerini, dil düzeyini ve işletmenin gerekliliklerini görüşürüz.',
      photoAlts: [
        'Baret ve reflektörlü yelek giyen inşaat çalışanları şantiyede.',
        'Bir usta iç mekânda alçıpan duvar montajı yapıyor.',
      ],
    },
    schweisser: {
      title: 'Kaynakçılık. Ustalığınla sağlam bağlar kur.',
      intro:
        'Metalle çalışmayı seviyor ve kaynak deneyimi taşıyor musun? Becerilerini bize anlat. Almanya’da sana uygun bir işe giden yolda yanında olalım.',
      areas: [
        ['Kaynak yöntemleri', 'MIG, MAG, TIG veya elektrot kaynağı deneyimini başvurunda paylaş.'],
        ['Metal işleri', 'Parçaları teknik resme göre hazırla, hizala ve sağlam yapılar oluştur.'],
        ['Kalite', 'Kaynakları özenle uygula, sonuçları kontrol et ve koruyucu önlemlere uy.'],
      ],
      preparation:
        'Kullandığın kaynak yöntemlerini, malzemeleri ve çalıştığın alanları belirt. Mevcut kaynak yeterlilik belgelerini ve sertifikalarını sunabilirsin. Belirli görevler için gereken sınavları işletmeyle netleştiririz; başvuru mesleki bir yetki belgesinin yerine geçmez.',
      photoAlts: [
        'Koruyucu maskeli kaynakçı atölyede bir metal parça üzerinde çalışıyor.',
        'Üretim salonunda kıvılcımlar eşliğinde kaynak işi yapılıyor.',
      ],
    },
    cnc: {
      title: 'CNC ve talaşlı imalat. Hassasiyetle geleceğe.',
      intro:
        'Tornalama, frezeleme, makine ayarı veya programlama: CNC deneyimini bize anlat. Becerilerinin Almanya’da hangi işe uyabileceğini birlikte değerlendirelim.',
      areas: [
        ['Tornalama ve frezeleme', 'Teknik resme göre uygun takımlarla parçaları işle.'],
        ['Makine ayarı', 'Takımları değiştir, programları devreye al ve işleme adımlarını izle.'],
        ['Ölçü hassasiyeti', 'Parçaları ölç, toleransları kontrol et ve üretim kalitesini belgele.'],
      ],
      preparation:
        'Çalıştığın makineleri, kontrol ünitelerini ve malzemeleri anlat. Makineleri kullanıp kullanmadığını, ayar yapıp yapmadığını veya program yazıp yazmadığını belirt. Eğitim ve çalışma belgeleri, işletmenin deneyimini değerlendirmesini kolaylaştırır.',
      photoAlts: [
        'Atölyedeki bir CNC makinesi iş parçasını işliyor.',
        'Atölyede bir torna tezgâhı metal iş parçasını işliyor.',
      ],
    },
    shk: {
      title: 'Sıhhi tesisat, ısıtma ve klimada geleceğin.',
      intro:
        'Su, sıcaklık ve iyi bir iç ortam: önemli sistemlerin çalışmasını sağlıyorsun. Sıhhi tesisat, ısıtma ve klima alanında Almanya’daki başlangıcın için başvuruna destek oluyoruz.',
      areas: [
        ['Sıhhi tesisat', 'Su borularını, armatürleri ve tesisat sistemlerini kur ve bakımını yap.'],
        ['Isıtma', 'Isıtma sistemlerini ve bileşenlerini kur, bakımını yap ve kontrol et.'],
        ['Klima ve servis', 'Havalandırma ve iklimlendirme sistemlerinde çalış, arızaları sistemli biçimde araştır.'],
      ],
      preparation:
        'Hangi sistemlerde çalıştığını ve odağının montaj, bakım veya teknik servis olup olmadığını belirt. Bazı işler için ek yeterlilik belgeleri gerekir. Pozisyonun gerekliliklerini eğitimini, dilini ve pratik deneyimini dikkate alarak önceden netleştiririz.',
      photoAlts: [
        'Bir teknisyen teknik odadaki ısıtma sistemini kontrol ediyor.',
        'Sıhhi tesisat uzmanı boru anahtarıyla bir boru üzerinde çalışıyor.',
      ],
    },
    'lkw-fahrer': {
      title: 'Kamyon şoförlüğü. Geleceğini yola çıkar.',
      intro:
        'Kamyon kullanma deneyimin mi var? Almanya’da taşımacılık alanına giden yolunu, net gereklilikler ve kişisel destekle birlikte konuşalım.',
      areas: [
        ['Yük taşımacılığı', 'Seferleri güvenilir biçimde hazırla ve yükleri güvenle ulaştır.'],
        ['Yükleme ve boşaltma', 'Depo ekibiyle birlikte çalış ve yükün doğru sabitlenmesine dikkat et.'],
        ['Araç ve planlama', 'Aracın durumunu, güzergâhları ve belgeleri takip et, açık iletişim kur.'],
      ],
      preparation:
        'Ehliyet sınıflarını, ehliyetin verildiği ülkeyi, sürüş deneyimini ve mesleki yeterlilik belgelerini belirt. İşe başlamadan önce ehliyet dönüşümü, mesleki sürücü yeterliliği ve ek belgeler bireysel olarak incelenmelidir. Başvuru, bu incelemenin ilk adımıdır.',
      photoAlts: [
        'Reflektörlü yelek giyen profesyonel sürücü bir kamyonun kabininde oturuyor.',
        'Reflektörlü kıyafetli kamyon şoförü lojistik alanında aracının yanında duruyor.',
      ],
    },
  },
};

const shared = {
  de: {
    apply: 'Jetzt bewerben',
    process: 'So funktioniert’s',
    areas: 'Dein Arbeitsalltag',
    areasTitle: 'Hier kannst du dein Können einbringen.',
    prepare: 'Dein Profil',
    prepareTitle: 'Erfahrung zeigen. Nächste Schritte klären.',
    route: 'Mit Viva Porta',
    routeTitle: 'Dein Weg wird persönlich begleitet.',
    routeText:
      'Wir beginnen mit deinem Profil. Welche Möglichkeiten sich daraus ergeben, hängt von deiner Erfahrung und den Anforderungen eines passenden Betriebs ab.',
    steps: [
      ['Bewerbung', 'Teile deine Erfahrung, deinen Sprachstand und deine Unterlagen im passenden Formular.'],
      [
        'Persönliches Gespräch',
        'Wir besprechen dein Profil, offene Fragen und die Anforderungen möglicher Arbeitgeber.',
      ],
      [
        'Vorbereitung und Start',
        'Wenn eine Zusammenarbeit passt, stimmen wir die nötigen Unterlagen und die nächsten Schritte mit dir ab.',
      ],
    ],
    faq: [
      [
        'Kann ich mich ohne Hochschulabschluss bewerben?',
        'Ja. Gib deine tatsächliche Ausbildung und praktische Erfahrung an. Ein Hochschulabschluss ist nicht für jede Tätigkeit erforderlich; die Voraussetzungen hängen von der konkreten Stelle ab.',
      ],
      [
        'Brauche ich schon Deutschkenntnisse?',
        'Gib deinen aktuellen Sprachstand ehrlich an. Welches Niveau erforderlich ist und welche Vorbereitung sinnvoll ist, klären wir passend zu deiner Erfahrung und der Stelle.',
      ],
      [
        'Habe ich mit der Bewerbung schon einen Job?',
        'Nein. Die Bewerbung hilft uns, dein Profil zu prüfen. Ein Arbeitsplatz, ein Starttermin oder ein Visum sind dadurch noch nicht zugesagt.',
      ],
    ],
    ctaTitle: 'Dein Können verdient eine Perspektive.',
    ctaText:
      'Mach den ersten Schritt und erzähle uns von deinem beruflichen Hintergrund. Wir schauen gemeinsam, wie es weitergehen kann.',
  },
  en: {
    apply: 'Apply now',
    process: 'How it works',
    areas: 'Your working day',
    areasTitle: 'Put your skills to work.',
    prepare: 'Your profile',
    prepareTitle: 'Show your experience. Clarify the next steps.',
    route: 'With Viva Porta',
    routeTitle: 'Personal guidance along the way.',
    routeText:
      'We start with your profile. The opportunities depend on your experience and the requirements of a suitable employer.',
    steps: [
      ['Application', 'Share your experience, language level and documents in the relevant form.'],
      ['Personal conversation', 'We discuss your profile, open questions and potential employers’ requirements.'],
      ['Preparation and start', 'If there is a suitable match, we agree on the documents and next steps together.'],
    ],
    faq: [
      [
        'Can I apply without a university degree?',
        'Yes. Describe your actual training and practical experience. Not every role requires a university degree; the requirements depend on the particular job.',
      ],
      [
        'Do I already need German skills?',
        'Give an honest account of your current level. We discuss the required level and suitable preparation based on your experience and the role.',
      ],
      [
        'Does applying mean I already have a job?',
        'No. Your application allows us to review your profile. It does not guarantee a job, start date or visa.',
      ],
    ],
    ctaTitle: 'Your skills deserve a future.',
    ctaText:
      'Take the first step and tell us about your professional background. Together, we explore what could come next.',
  },
  tr: {
    apply: 'Hemen başvur',
    process: 'Süreç nasıl işliyor?',
    areas: 'Çalışma hayatın',
    areasTitle: 'Becerilerini bu alanlarda kullanabilirsin.',
    prepare: 'Profilin',
    prepareTitle: 'Deneyimini anlat. Sonraki adımları netleştir.',
    route: 'Viva Porta ile',
    routeTitle: 'Yol boyunca kişisel destek.',
    routeText: 'Profilinle başlarız. Olası fırsatlar, deneyimine ve uygun bir işletmenin gerekliliklerine bağlıdır.',
    steps: [
      ['Başvuru', 'İlgili formda deneyimini, dil düzeyini ve belgelerini paylaş.'],
      ['Kişisel görüşme', 'Profilini, sorularını ve olası işverenlerin gerekliliklerini birlikte konuşuruz.'],
      ['Hazırlık ve başlangıç', 'Uygun bir eşleşme olduğunda gerekli belgeleri ve sonraki adımları seninle planlarız.'],
    ],
    faq: [
      [
        'Üniversite diploması olmadan başvurabilir miyim?',
        'Evet. Gerçek eğitimini ve pratik deneyimini belirt. Her iş üniversite diploması gerektirmez; koşullar pozisyona bağlıdır.',
      ],
      [
        'Önceden Almanca bilmem gerekiyor mu?',
        'Mevcut dil düzeyini dürüstçe belirt. Gereken düzeyi ve uygun hazırlığı deneyimine ve pozisyona göre birlikte netleştiririz.',
      ],
      [
        'Başvurunca işim hazır mı oluyor?',
        'Hayır. Başvuru, profilini incelememizi sağlar. Bir iş, başlangıç tarihi veya vize garantisi vermez.',
      ],
    ],
    ctaTitle: 'Becerilerin yeni bir geleceği hak ediyor.',
    ctaText: 'İlk adımı at ve mesleki geçmişini anlat. Nasıl ilerleyebileceğimize birlikte bakalım.',
  },
} as const;

export const industryApplicationHref = (key: IndustryPageKey, locale?: LandingLocale) =>
  `${resolveApplicationPortalBase()}${applicationPath(key, locale ?? 'de')}${locale ? `?lang=${locale}` : ''}`;

export const industryOverviewCopy = (locale: LandingLocale) => ({
  cardsEyebrow: shared[locale].areas,
  cardsTitle: shared[locale].areasTitle,
});

export const industryGalleryDefaults = (key: IndustryPageKey, locale: LandingLocale): StaticPageHeroGalleryItem[] => {
  if(isCareIndustryPageKey(key))return createCareIndustryPages(locale)[key].heroGallery!;
  if(isRetailIndustryPageKey(key))return createRetailIndustryPages(locale)[key].heroGallery!;
  const details = (copy[locale] as Partial<Record<IndustryPageKey, IndustryCopy>>)[key];
  const existing = existingPhotoCopy[locale][key as keyof typeof existingPhotoCopy.de];
  return photos[key].map((image, index) => ({
    image,
    imageAlt: details?.photoAlts[index] ?? existing?.[index]?.[1] ?? `${industryPageLabel(key, locale)} · ${index + 1}`,
    title:
      details?.areas[key === 'shk' ? 1 - index : index]?.[0] ??
      existing?.[index]?.[0] ??
      industryPageLabel(key, locale),
  }));
};

export const createNewIndustryPages = (locale: LandingLocale = 'de'): Record<NewIndustryPageKey, StaticPageContent> => {
  const result = {} as Record<NewIndustryPageKey, StaticPageContent>;
  const ui = shared[locale];
  for (const key of newIndustryPageKeys) {
    const detail = copy[locale][key];
    const action = { label: ui.apply, href: industryApplicationHref(key, locale) };
    result[key] = {
      key,
      eyebrow: industryPageLabel(key, locale),
      title: detail.title,
      intro: detail.intro,
      ...industryOverviewCopy(locale),
      heroImage: photos[key][0],
      heroGallery: industryGalleryDefaults(key, locale),
      heroAction: action,
      heroHighlights: detail.areas.map(area => area[0]),
      cards: detail.areas.map(([title, text]) => ({
        title,
        text,
        iconClass: 'icon-[material-symbols--check-circle-outline-rounded]',
      })),
      sections: [
        {
          eyebrow: ui.prepare,
          title: ui.prepareTitle,
          text: detail.preparation,
          image: photos[key][1],
          imageAlt: detail.photoAlts[1],
        },
        {
          eyebrow: ui.route,
          title: ui.routeTitle,
          text: ui.routeText,
          cards: ui.steps.map(([title, text]: readonly [string, string]) => ({
            title,
            text,
            iconClass: 'icon-[material-symbols--arrow-forward-rounded]',
          })),
        },
      ],
      faq: ui.faq.map(([question, answer]: readonly [string, string]) => ({ question, answer })),
      cta: {
        eyebrow: industryPageLabel(key, locale),
        title: ui.ctaTitle,
        text: ui.ctaText,
        primaryAction: action,
        secondaryAction: { label: ui.process, href: '/ablauf' },
      },
    };
  }
  return result;
};
